import React from 'react';
import { Box, Chip, LinearProgress, Tooltip, Typography, useTheme } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { type KPILimit, useKPIStore } from '../../stores/kpi.store';
import { findEnabledKpiLimit } from '../../utils/metricPolarity';

interface KPIProgressCardProps {
  title: string;
  /** This month's reading; null when the month has no value for the KPI. */
  value: number | null;
  unit: string;
  /** Canonical metric key (StatisticType) — preferred over title matching. */
  metricKey?: string;
  /** The rule this card tracks (one card per limit); looked up by key when omitted. */
  limit?: KPILimit;
}

function meetsTarget(value: number, limit: number, operator: string): boolean {
  switch (operator) {
    case '>':
      return value > limit;
    case '>=':
      return value >= limit;
    case '<':
      return value < limit;
    case '<=':
      return value <= limit;
    case '=':
      return Math.abs(value - limit) < 0.01;
    default:
      return true;
  }
}

const OPERATOR_SYMBOL: Record<string, string> = { '>': '>', '<': '<', '>=': '≥', '<=': '≤', '=': '=' };

/** Flat target row: label, value vs. target, 4px progress and a status chip. */
const KPIProgressCard: React.FC<KPIProgressCardProps> = ({ title, value, unit, metricKey, limit }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const { limits } = useKPIStore();

  const kpiLimit =
    limit ??
    findEnabledKpiLimit(limits, metricKey) ??
    limits.find((limit) => {
      const normalizedTitle = title.replace(/[\s_]/g, '').toUpperCase();
      const normalizedLimitName = limit.name.replace(/[\s_]/g, '').toUpperCase();
      return normalizedLimitName === normalizedTitle && limit.isEnabled;
    });

  if (!kpiLimit) return null;

  const hasValue = value !== null;
  const onTarget = hasValue && meetsTarget(value, kpiLimit.value, kpiLimit.operator);
  const progress = !hasValue
    ? 0
    : kpiLimit.operator === '='
      ? onTarget
        ? 100
        : 0
      : Math.max(0, Math.min((value / (kpiLimit.value || 1)) * 100, 100));
  const statusColor = !hasValue
    ? theme.palette.text.secondary
    : onTarget
      ? theme.palette.success.main
      : theme.palette.error.main;
  const statusLabel = !hasValue
    ? t('common.noData')
    : onTarget
      ? t('dashboard.targets.onTarget')
      : t('dashboard.targets.offTarget');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0 }} noWrap>
          {title.replace(/_/g, ' ')}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          icon={!hasValue ? undefined : onTarget ? <CheckCircleOutline /> : <ErrorOutline />}
          label={statusLabel}
          sx={{
            color: statusColor,
            borderColor: statusColor,
            '& .MuiChip-icon': { color: statusColor, fontSize: 14 },
          }}
        />
      </Box>
      <Tooltip title={kpiLimit.alertMessage}>
        <Typography
          component="p"
          sx={{ fontSize: '1.25rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
        >
          <bdi>{hasValue ? fmt.number(value) : '—'}</bdi>
          <Box component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', mx: 0.75 }}>
            {/* LTR isolate so "> 80" never mirrors to "80 <" in RTL. */}
            <Box component="bdi" dir="ltr" sx={{ unicodeBidi: 'isolate' }}>
              {OPERATOR_SYMBOL[kpiLimit.operator] ?? kpiLimit.operator} {fmt.number(kpiLimit.value)}
            </Box>{' '}
            {unit}
          </Box>
        </Typography>
      </Tooltip>
      <LinearProgress
        variant="determinate"
        value={progress}
        aria-label={`${title}: ${Math.round(progress)}%`}
        sx={{ '& .MuiLinearProgress-bar': { backgroundColor: statusColor, opacity: hasValue ? 1 : 0 } }}
      />
    </Box>
  );
};

export default KPIProgressCard;
